import {
  ServiceRequest,
  ServiceRequestResponse,
  ServiceRequestCategory,
  ServiceRequestPriority,
  ServiceRequestStatus,
} from '@/types/shared/serviceRequest';

export interface CreateServiceRequestData {
  title: string;
  description: string;
  category: ServiceRequestCategory;
  priority: ServiceRequestPriority;
  location?: {
    building?: string;
    unit?: string;
    commonArea?: string;
  };
  budget?: {
    min?: number;
    max?: number;
    currency: string;
  };
  timeline?: {
    preferredStartDate?: string;
    deadline?: string;
    estimatedDuration?: string;
  };
  attachments?: File[];
}

export interface CreateServiceRequestResponseData {
  serviceRequestId: string;
  message: string;
  estimatedCost?: number;
  estimatedDuration?: string;
  availability?: string;
  certifications?: string[];
  portfolioItems?: {
    title: string;
    description: string;
    imageFile?: File;
    imageUrl?: string;
  }[];
}

export interface UpdateServiceRequestData {
  title?: string;
  description?: string;
  category?: ServiceRequestCategory;
  priority?: ServiceRequestPriority;
  status?: ServiceRequestStatus;
  location?: {
    building?: string;
    unit?: string;
    commonArea?: string;
  };
  budget?: {
    min?: number;
    max?: number;
    currency: string;
  };
  timeline?: {
    preferredStartDate?: string;
    deadline?: string;
    estimatedDuration?: string;
  };
}

export interface ServiceRequestFilters {
  category?: ServiceRequestCategory;
  priority?: ServiceRequestPriority;
  status?: ServiceRequestStatus;
  dateRange?: {
    start: string;
    end: string;
  };
  budgetRange?: {
    min: number;
    max: number;
  };
}

class ServiceRequestService {
  private baseUrl = '/api/service-requests';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';
  private isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';

  async getServiceRequests(filters?: ServiceRequestFilters): Promise<ServiceRequest[]> {
    // In development mode without backend, return mock data directly
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockServiceRequests();
    }

    try {
      const queryParams = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
              queryParams.append(key, JSON.stringify(value));
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }

      const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch service requests: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching service requests:', error);
      // Return mock data for development
      return this.getMockServiceRequests();
    }
  }

  async getServiceRequestById(id: string): Promise<ServiceRequest> {
    // In development mode without backend, return mock data directly
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      const mockRequests = this.getMockServiceRequests();
      const request = mockRequests.find((req) => req.id === id);
      if (!request) {
        throw new Error(`Service request with id ${id} not found`);
      }
      return request;
    }

    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch service request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching service request:', error);
      // Return mock data for development
      const mockRequests = this.getMockServiceRequests();
      const request = mockRequests.find((req) => req.id === id);
      if (!request) {
        throw new Error(`Service request with id ${id} not found`, { cause: error });
      }
      return request;
    }
  }

  async createServiceRequest(data: CreateServiceRequestData): Promise<ServiceRequest> {
    try {
      const formData = new FormData();

      // Add basic data
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('priority', data.priority);

      if (data.location) {
        formData.append('location', JSON.stringify(data.location));
      }

      if (data.budget) {
        formData.append('budget', JSON.stringify(data.budget));
      }

      if (data.timeline) {
        formData.append('timeline', JSON.stringify(data.timeline));
      }

      // Add file attachments
      if (data.attachments) {
        data.attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to create service request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating service request:', error);
      throw error;
    }
  }

  async createServiceRequestResponse(
    data: CreateServiceRequestResponseData,
  ): Promise<ServiceRequestResponse> {
    // In development mode without backend, return mock response directly
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.createMockResponse(data);
    }

    try {
      const formData = new FormData();

      // Add basic response data
      formData.append('serviceRequestId', data.serviceRequestId);
      formData.append('message', data.message);

      if (data.estimatedCost) {
        formData.append('estimatedCost', data.estimatedCost.toString());
      }

      if (data.estimatedDuration) {
        formData.append('estimatedDuration', data.estimatedDuration);
      }

      if (data.availability) {
        formData.append('availability', data.availability);
      }

      if (data.certifications) {
        formData.append('certifications', JSON.stringify(data.certifications));
      }

      // Add portfolio items and their images
      if (data.portfolioItems) {
        data.portfolioItems.forEach((item, index) => {
          formData.append(`portfolioItems[${index}][title]`, item.title);
          formData.append(`portfolioItems[${index}][description]`, item.description);

          if (item.imageFile) {
            formData.append(`portfolioItems[${index}][image]`, item.imageFile);
          } else if (item.imageUrl) {
            formData.append(`portfolioItems[${index}][imageUrl]`, item.imageUrl);
          }
        });
      }

      const response = await fetch(`${this.baseUrl}/${data.serviceRequestId}/responses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to create service request response: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating service request response:', error);
      throw error;
    }
  }

  async updateServiceRequest(id: string, data: UpdateServiceRequestData): Promise<ServiceRequest> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to update service request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating service request:', error);
      throw error;
    }
  }

  async deleteServiceRequest(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete service request: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting service request:', error);
      throw error;
    }
  }

  async uploadFile(file: File): Promise<{ url: string; id: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Mock data for development
  private getMockServiceRequests(): ServiceRequest[] {
    return [
      {
        id: 'sr-001',
        title: 'HVAC Emergency Repair',
        description:
          'Unit 302 - Air conditioning not working, urgent repair needed. The AC has been making strange noises and stopped cooling completely.',
        category: 'hvac',
        priority: 'urgent',
        status: 'open',
        requestedBy: {
          id: 'user-001',
          name: 'Sarah Johnson',
          role: 'resident' as const,
          unitNumber: '302',
          building: 'Building A',
        },
        location: {
          building: 'Building A',
          unit: '302',
        },
        budget: {
          min: 200,
          max: 800,
          currency: 'USD',
        },
        timeline: {
          deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          estimatedDuration: '2-4 hours',
        },
        attachments: [],
        responses: [],
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'sr-002',
        title: 'Pool Filter Replacement',
        description:
          'Community pool needs filter replacement and cleaning. The water has been getting cloudy and the current filter is overdue for replacement.',
        category: 'general_maintenance',
        priority: 'medium',
        status: 'open',
        requestedBy: {
          id: 'user-002',
          name: 'Mike Chen',
          role: 'resident' as const,
          unitNumber: '105',
        },
        location: {
          commonArea: 'Community Pool Area',
        },
        budget: {
          min: 150,
          max: 300,
          currency: 'USD',
        },
        timeline: {
          preferredStartDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          estimatedDuration: '1-2 hours',
        },
        attachments: [],
        responses: [],
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
  }

  private createMockResponse(data: CreateServiceRequestResponseData): ServiceRequestResponse {
    return {
      id: `response-${Math.random().toString(36).substr(2, 9)}`,
      serviceRequestId: data.serviceRequestId,
      vendorId: 'vendor-123',
      vendorName: 'ProFix Services',
      vendorRating: 4.8,
      message: data.message,
      estimatedCost: data.estimatedCost,
      estimatedDuration: data.estimatedDuration,
      availability: data.availability,
      certifications: data.certifications || [],
      portfolio:
        data.portfolioItems?.map((item) => ({
          id: `portfolio-${Math.random().toString(36).substr(2, 9)}`,
          title: item.title,
          description: item.description,
          imageUrl: item.imageFile ? URL.createObjectURL(item.imageFile) : undefined,
          completedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        })) || [],
      status: 'pending' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
}

export const serviceRequestService = new ServiceRequestService();
